import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import DepartmentSettings from './DepartmentSettings';
import BillingDashboard from './BillingDashboard';
import CommunicationPortal from './CommunicationPortal';
import MonitoringDashboard from './MonitoringDashboard';
import SubdomainManager from './SubdomainManager';
import { 
  Building2,
  CreditCard, 
  Palette,
  Megaphone,
  Activity,
  Globe,
  Settings,
  BarChart3,
  LogOut,
  User,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const OwnerDashboard = () => {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [department, setDepartment] = useState(null);
  
  const { user, API, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    fetchOwnerStats();
    fetchDepartmentInfo();
  }, []);

  const fetchOwnerStats = async () => {
    try {
      const response = await axios.get(`${API}/owner/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching owner stats:', error);
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartmentInfo = async () => {
    try {
      const response = await axios.get(`${API}/departments/current`);
      setDepartment(response.data);
    } catch (error) {
      console.error('Error fetching department info:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" style={{
      backgroundColor: department?.branding?.background_color || '#ffffff'
    }}>
      {/* Header */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center gap-4">
          {/* Department Logo or Building Icon */}
          {department?.logo_url ? (
            <img 
              src={department.logo_url.startsWith('http') ? department.logo_url : 
                  department.logo_url.startsWith('/api/') ? 
                    `${API.replace('/api', '')}${department.logo_url}` : 
                    `${API}/departments/logo/${department.logo_url}`}
              alt={`${department.name} Logo`}
              className="w-12 h-12 object-contain rounded"
              onError={(e) => {
                console.log('Logo failed to load, using fallback icon');
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
          ) : null}
          <Building2 
            className="w-8 h-8 text-blue-600" 
            style={{ 
              display: department?.logo_url ? 'none' : 'block',
              color: department?.branding?.primary_color || '#2563eb'
            }} 
          />
          
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Owner Dashboard</h1>
            <p className="text-slate-600">
              Department management, branding, and billing oversight
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {department && (
            <div className="text-right">
              <p className="text-sm text-slate-500">Department</p>
              <p className="font-medium text-slate-800">{department.name}</p>
            </div>
          )}
          
          <div className="h-6 w-px bg-gray-300" />
          
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-slate-600" />
            <span className="text-sm font-medium text-slate-700">{user?.full_name}</span>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="flex items-center gap-2 text-red-600 hover:text-red-700"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList 
          className="grid w-full grid-cols-6 gap-1"
          style={{
            backgroundColor: department?.branding?.secondary_color || '#f1f5f9'
          }}
        >
          <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
          <TabsTrigger value="branding" className="text-xs">Branding</TabsTrigger>
          <TabsTrigger value="billing" className="text-xs">Billing</TabsTrigger>
          <TabsTrigger value="communication" className="text-xs">Communications</TabsTrigger>
          <TabsTrigger value="monitoring" className="text-xs">Monitoring</TabsTrigger>
          <TabsTrigger value="subdomains" className="text-xs">Subdomains</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Department Health */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Department Status</CardTitle>
                <Building2 className="w-4 h-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">Active</div>
                <p className="text-xs text-muted-foreground">
                  {stats.subscription_status || 'Trial'} subscription
                </p>
              </CardContent>
            </Card>

            {/* Total Users */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                <BarChart3 className="w-4 h-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total_users || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.staff_count || 0} staff, {stats.citizen_count || 0} citizens
                </p>
              </CardContent>
            </Card>

            {/* Monthly Requests */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Monthly Requests</CardTitle>
                <Activity className="w-4 h-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.monthly_requests || 0}</div>
                <p className="text-xs text-muted-foreground">
                  +{stats.request_growth || 0}% from last month
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>
                Common owner tasks and department management
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Button 
                  variant="outline" 
                  className="h-20 flex-col gap-2 hover:bg-opacity-10"
                  onClick={() => setActiveTab('branding')}
                  style={{
                    borderColor: department?.branding?.primary_color || '#3b82f6',
                    color: department?.branding?.primary_color || '#3b82f6'
                  }}
                >
                  <Palette className="w-6 h-6" />
                  <span className="text-xs">Update Branding</span>
                </Button>
                
                <Button 
                  variant="outline" 
                  className="h-20 flex-col gap-2 hover:bg-opacity-10"
                  onClick={() => setActiveTab('billing')}
                  style={{
                    borderColor: department?.branding?.primary_color || '#3b82f6',
                    color: department?.branding?.primary_color || '#3b82f6'
                  }}
                >
                  <CreditCard className="w-6 h-6" />
                  <span className="text-xs">Manage Billing</span>
                </Button>
                
                <Button 
                  variant="outline" 
                  className="h-20 flex-col gap-2 hover:bg-opacity-10"
                  onClick={() => setActiveTab('subdomains')}
                  style={{
                    borderColor: department?.branding?.primary_color || '#3b82f6',
                    color: department?.branding?.primary_color || '#3b82f6'
                  }}
                >
                  <Globe className="w-6 h-6" />
                  <span className="text-xs">Subdomains</span>
                </Button>
                
                <Button 
                  variant="outline" 
                  className="h-20 flex-col gap-2 hover:bg-opacity-10"
                  onClick={() => setActiveTab('monitoring')}
                  style={{
                    borderColor: department?.branding?.primary_color || '#3b82f6',
                    color: department?.branding?.primary_color || '#3b82f6'
                  }}
                >
                  <Activity className="w-6 h-6" />
                  <span className="text-xs">View Monitoring</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Department Branding & Settings
              </CardTitle>
              <CardDescription>
                Customize your department's appearance and public-facing information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DepartmentSettings />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Billing & Subscription Management
              </CardTitle>
              <CardDescription>
                Manage your subscription, view invoices, and upgrade plans
              </CardDescription>
            </CardHeader>
            <CardContent>
              <BillingDashboard />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Communication Tab */}
        <TabsContent value="communication">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="w-5 h-5" />
                Department Communications
              </CardTitle>
              <CardDescription>
                Send announcements and manage department communications
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CommunicationPortal />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Monitoring Tab */}
        <TabsContent value="monitoring">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                System Monitoring
              </CardTitle>
              <CardDescription>
                Monitor system health, performance, and usage analytics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <MonitoringDashboard />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subdomains Tab */}
        <TabsContent value="subdomains">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Subdomain Management
              </CardTitle>
              <CardDescription>
                Manage DNS subdomains and department web presence
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SubdomainManager />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OwnerDashboard;